"use client"

import { cn } from "@/lib/utils"
import {
  Button as MantineButton,
  Skeleton,
  type ButtonProps as MantineButtonProps,
  type ButtonStylesNames,
} from "@mantine/core"
import classes from "./glass-button.module.css"

export type GlassButtonSize = "sm" | "md" | "lg"

const SIZE_CLASS: Record<GlassButtonSize, string> = {
  sm: classes.sizeSm,
  md: classes.sizeMd,
  lg: classes.sizeLg,
}

const SKELETON_SIZE_CLASS: Record<GlassButtonSize, string> = {
  sm: classes.skeletonSm,
  md: classes.skeletonMd,
  lg: classes.skeletonLg,
}

type GlassButtonOwnProps = {
  size?: GlassButtonSize
  fullWidth?: boolean
  /** Shows a skeleton shimmer instead of the button */
  isLoading?: boolean
  /** Icon node rendered to the left of the label */
  leftIcon?: React.ReactNode
  /** Icon node rendered to the right of the label */
  rightIcon?: React.ReactNode
  /** When true, renders a red notification badge on the top-right corner */
  showNotification?: boolean
  /** Number shown inside the notification badge */
  notificationCount?: number
  className?: string
  classNames?: Partial<Record<ButtonStylesNames, string>>
}

export type GlassButtonProps = Omit<
  MantineButtonProps,
  | "variant"
  | "size"
  | "color"
  | "radius"
  | "gradient"
  | "leftSection"
  | "rightSection"
  | "loading"
  | "loaderProps"
  | "unstyled"
  | "classNames"
> &
  GlassButtonOwnProps &
  Omit<React.ComponentPropsWithoutRef<"button">, keyof GlassButtonOwnProps>

/**
 * Glass-style design-system button (Figma StreamHub UI Kit).
 * Built on Mantine `Button` with styles in `glass-button.module.css`.
 */
export function GlassButton({
  size = "sm",
  fullWidth = false,
  isLoading = false,
  leftIcon,
  rightIcon,
  showNotification = false,
  notificationCount = 1,
  disabled,
  children,
  className,
  classNames,
  ...props
}: GlassButtonProps) {
  if (isLoading) {
    return (
      <Skeleton
        className={cn(
          classes.skeleton,
          size === "lg" && classes.skeletonLgRadius,
          SKELETON_SIZE_CLASS[size],
          fullWidth && classes.skeletonFullWidth,
          className
        )}
      />
    )
  }

  const rootClassName = cn(
    classes.root,
    SIZE_CLASS[size],
    fullWidth && classes.fullWidth,
    className,
    classNames?.root
  )

  return (
    <span className={classes.wrapper}>
      <MantineButton
        data-slot="glass-button"
        unstyled
        disabled={disabled}
        fullWidth={fullWidth}
        leftSection={leftIcon}
        rightSection={rightIcon}
        classNames={{
          root: rootClassName,
          inner: cn(classes.inner, classNames?.inner),
          label: cn(classes.label, classNames?.label),
          section: cn(classes.section, classNames?.section),
          loader: classNames?.loader,
        }}
        {...props}
      >
        {children}
      </MantineButton>

      {showNotification && (
        <span
          aria-label={`${notificationCount} notification${notificationCount !== 1 ? "s" : ""}`}
          className={classes.badge}
        >
          <span className={classes.badgeCount}>{notificationCount}</span>
        </span>
      )}
    </span>
  )
}
